import React, { useState } from 'react';
import { X, Gift, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const RegistrationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/registrations`, formData);
      
      // Show success toast first
      toast.success("Registrazione completata!", {
        description: `Hai risparmiato ${response.data.discount} + ${response.data.freeVisit} di prima uscita gratuita!`,
        duration: 5000,
      });
      
      // Show success state
      setIsSubmitted(true);

      // Reset and close after showing toast
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ firstName: '', lastName: '', email: '', phone: '' });
        onClose();
      }, 2000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Si è verificato un errore. Riprova.";
      toast.error("Errore nella registrazione", {
        description: errorMessage,
        duration: 5000,
      });
      // Don't close modal on error so user can see the message and retry
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        {!isSubmitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Registrati e Risparmia</DialogTitle>
              <DialogDescription className="text-base">
                Completa la registrazione per ottenere i tuoi vantaggi esclusivi
              </DialogDescription>
            </DialogHeader>

            {/* Benefits */}
            <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-lg p-4 my-4">
              <div className="flex items-center gap-3 mb-3">
                <Gift className="w-6 h-6 text-orange-600" />
                <h3 className="font-bold text-gray-900">I Tuoi Vantaggi:</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>10€ di sconto</strong> sul primo intervento</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Prima uscita gratuita</strong> (valore 30€)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Preventivi prioritari</span>
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-orange-200">
                <p className="text-sm font-bold text-orange-600">Totale Risparmio: 40€</p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Nome</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Cognome</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefono</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white" disabled={isLoading}>
                {isLoading ? 'Invio in corso...' : 'Completa Registrazione'}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Registrandoti accetti di ricevere comunicazioni commerciali
              </p>
            </form>
          </>
        ) : (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Registrazione Completata!</h3>
            <p className="text-gray-600">Ti contatteremo presto per confermare i tuoi vantaggi.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
